import { fetchAllInstances } from '@/requests/api/fetch-all-instances';
import type {
  Apartment,
  BrfApartment,
  Building,
  CommonArea,
  Component,
  IndustrialPremises,
  OutdoorSection,
  ParkingSpot,
  RealEstate,
} from '@pigello/pigello-matrix';
import { chunkIds, isThin } from '../utils';
import type { TCustomList } from './types';

/**
 * Connected object keys that can exist on a Component.
 */
const CONNECTED_OBJECT_KEYS = [
  'apartment',
  'industrialPremises',
  'parkingSpot',
  'commonArea',
  'brfApartment',
  'outdoorSection',
] as const;

type ConnectedObjectKey = (typeof CONNECTED_OBJECT_KEYS)[number];

type ConnectedObjectModelName =
  | 'apartment'
  | 'industrialpremises'
  | 'parkingspot'
  | 'commonarea'
  | 'brfapartment'
  | 'outdoorsection';

const KEY_TO_MODEL_NAME: Record<ConnectedObjectKey, ConnectedObjectModelName> =
  {
    apartment: 'apartment',
    industrialPremises: 'industrialpremises',
    parkingSpot: 'parkingspot',
    commonArea: 'commonarea',
    brfApartment: 'brfapartment',
    outdoorSection: 'outdoorsection',
  };

type ConnectedObjectInstance =
  | Apartment
  | BrfApartment
  | IndustrialPremises
  | ParkingSpot
  | OutdoorSection
  | CommonArea;

function hasBuilding(obj: unknown): obj is { building: { id: string } } {
  return (
    obj != null &&
    typeof obj === 'object' &&
    'building' in obj &&
    obj.building != null &&
    typeof obj.building === 'object' &&
    'id' in obj.building
  );
}

function getBuildingId(obj: unknown): string | undefined {
  return hasBuilding(obj) ? obj.building.id : undefined;
}

/**
 * Custom nested case handler for Component.
 * Handles connected objects and buildings (with realestate), works with or without room.
 */
export async function handleCustomComponentNestedCase(
  listResponse: TCustomList<Component>
): Promise<TCustomList<Component>> {
  const buildingIds = new Set<string>();
  const realestateIds = new Set<string>();
  const connectedObjectIdsByKey = new Map<ConnectedObjectKey, Set<string>>();

  for (const key of CONNECTED_OBJECT_KEYS) {
    connectedObjectIdsByKey.set(key, new Set());
  }

  // Collect all connected object IDs and building IDs
  for (const component of listResponse.data) {
    if (isThin(component.building)) buildingIds.add(component.building.id);
    if (isThin(component.realestate))
      realestateIds.add(component.realestate.id);

    for (const key of CONNECTED_OBJECT_KEYS) {
      const obj = component[key];
      if (isThin(obj)) {
        connectedObjectIdsByKey.get(key)?.add(obj.id);
      } else {
        const bid = getBuildingId(obj);
        if (bid) buildingIds.add(bid);
      }
    }

    // Connected objects on room
    if (component.room) {
      for (const key of CONNECTED_OBJECT_KEYS) {
        const obj = component.room[key as keyof typeof component.room];
        if (isThin(obj)) {
          connectedObjectIdsByKey.get(key)?.add(obj.id);
        } else {
          const bid = getBuildingId(obj);
          if (bid) buildingIds.add(bid);
        }
      }
    }
  }

  const connectedObjectMaps = new Map<
    ConnectedObjectKey,
    Map<string, ConnectedObjectInstance>
  >();

  for (const key of CONNECTED_OBJECT_KEYS) {
    const ids = connectedObjectIdsByKey.get(key);
    if (!ids || ids.size === 0) continue;

    const modelName = KEY_TO_MODEL_NAME[key];

    const chunkedIds = chunkIds(ids);

    for (const chunk of chunkedIds) {
      const res = await fetchAllInstances<ConnectedObjectInstance>({
        modelName,
        filters: { id: { __in: chunk.join(',') } },
      });
      let map = connectedObjectMaps.get(key);
      if (!map) {
        map = new Map();
        connectedObjectMaps.set(key, map);
      }
      for (const obj of res.list) {
        map.set(obj.id, obj);
        const bid = getBuildingId(obj);
        if (bid) buildingIds.add(bid);
      }
    }
  }

  // Fetch buildings and realestates
  const buildingMap = new Map<string, Building>();
  const realestateMap = new Map<string, RealEstate>();

  if (buildingIds.size > 0) {
    const chunkedBuildingIds = chunkIds(buildingIds);

    for (const chunk of chunkedBuildingIds) {
      const res = await fetchAllInstances<Building>({
        modelName: 'building',
        filters: { id: { __in: chunk.join(',') } },
        nested: ['realestate'],
      });

      for (const building of res.list) {
        buildingMap.set(building.id, building);
        if (building.realestate?.id) {
          realestateMap.set(building.realestate.id, building.realestate);
        }
      }
    }
  }

  if (realestateIds.size > 0) {
    const chunkedRealestateIds = chunkIds(realestateIds);

    for (const chunk of chunkedRealestateIds) {
      const res = await fetchAllInstances<RealEstate>({
        modelName: 'realestate',
        filters: { id: { __in: chunk.join(',') } },
      });

      for (const re of res.list) {
        realestateMap.set(re.id, re);
      }
    }
  }

  const mapped = listResponse.data.map((component) => {
    const updated = { ...component };

    // Merge building on component
    if (isThin(component.building)) {
      const full = buildingMap.get(component.building.id);
      if (full) updated.building = full;
    }
    if (isThin(component.realestate)) {
      const full = realestateMap.get(component.realestate.id);
      if (full) updated.realestate = full;
    }

    // Merge connected objects on component
    for (const key of CONNECTED_OBJECT_KEYS) {
      const obj = component[key];
      if (!obj) continue;

      const map = connectedObjectMaps.get(key);
      let full: ConnectedObjectInstance | undefined = isThin(obj)
        ? map?.get(obj.id)
        : (obj as ConnectedObjectInstance);

      const bid = getBuildingId(full);
      if (bid) {
        const fullBuilding = buildingMap.get(bid);
        if (fullBuilding && full) {
          full = { ...full, building: fullBuilding } as ConnectedObjectInstance;
        }
      }

      if (full) (updated as any)[key] = full;
    }

    // Merge connected objects on room
    if (component.room) {
      const updatedRoom = { ...component.room };
      let roomUpdated = false;

      for (const key of CONNECTED_OBJECT_KEYS) {
        const obj = component.room[key as keyof typeof component.room];
        if (!obj) continue;

        const map = connectedObjectMaps.get(key);
        let full: ConnectedObjectInstance | undefined = isThin(obj)
          ? map?.get(obj.id)
          : (obj as unknown as ConnectedObjectInstance);

        const bid = getBuildingId(full);
        if (bid) {
          const fullBuilding = buildingMap.get(bid);
          if (fullBuilding && full) {
            full = {
              ...full,
              building: fullBuilding,
            } as ConnectedObjectInstance;
          }
        }

        if (full) {
          (updatedRoom as any)[key] = full;
          roomUpdated = true;
        }
      }

      if (roomUpdated) updated.room = updatedRoom as typeof component.room;
    }

    return updated;
  });

  return { data: mapped };
}
